import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { KeyRound, RefreshCw } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input, { FieldLabel, FieldGroup, FieldHint } from '../ui/Input';
import { resetUserPassword } from '../../utils/api';

// Generate a policy-compliant temporary password (letters + digits, 12 chars).
function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  let out = '';
  for (let i = 0; i < 9; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  for (let i = 0; i < 3; i += 1) out += digits[Math.floor(Math.random() * digits.length)];
  return out;
}

export default function ResetPasswordModal({ open, user, onClose }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open || !user) return null;

  async function submit() {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await resetUserPassword(user.id, password);
      toast.success(`Password reset for @${user.username}`);
      setPassword('');
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Reset failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title="Reset password"
      description={`@${user.username}`}
      icon={KeyRound}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            Set password
          </Button>
        </>
      }
    >
      <FieldGroup>
        <FieldLabel>New temporary password</FieldLabel>
        <Input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 chars, with a letter and number"
          disabled={saving}
          rightSlot={
            <button
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[11.5px] text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <RefreshCw className="h-3 w-3" /> Generate
            </button>
          }
        />
        <FieldHint>
          The user will be required to change this on next sign-in.
        </FieldHint>
      </FieldGroup>
    </Modal>
  );
}
